import { inject, Injectable } from '@angular/core';
import { EMPTY, expand, Observable, reduce } from 'rxjs';

import {
  FetchInMonthExpensesInput,
  fetchInMonthExpensesQuery,
  GqlService,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';

type MonthExpenseItems = fetchInMonthExpensesQuery['items'];

@Injectable()
export class MonthExpensesService {
  private readonly gqlService = inject(GqlService);
  private readonly overlayService = inject(OverlayService);

  private readonly OFFSET_LIMIT = 500;

  fetchMonthExpenses$(
    vendors: null | string[],
    period: string
  ): Observable<GraphqlResponse<MonthExpenseItems>> {
    const input: FetchInMonthExpensesInput = {
      args: {
        organization_ids: vendors,
        period,
      },
      page: {
        limit: this.OFFSET_LIMIT,
        offset: 0,
      },
    };

    return this.gqlService.fetchInMonthExpenses$(input).pipe(
      expand((response) => {
        if (response.data?.next_offset === -1) {
          return EMPTY;
        }

        if (response.errors.length) {
          this.overlayService.error(response.errors);

          return EMPTY;
        }

        input.page.offset = response.data?.next_offset || 0;

        return this.gqlService.fetchInMonthExpenses$(input);
      }),
      reduce(
        (acc, curr) => {
          if (curr.errors.length) {
            return {
              data: [],
              errors: acc.errors?.concat(curr.errors || []) || [],
              success: false,
            };
          }

          return {
            ...acc,
            data: acc.data?.concat(curr.data?.items || []) || [],
            errors: acc.errors?.concat(curr.errors || []) || [],
            success: acc.success && curr.success,
          };
        },
        { data: [], errors: [], success: true } as GraphqlResponse<MonthExpenseItems>
      )
    );
  }
}
